import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@repo/ui/collapsible'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { cn, sortByKey } from '@repo/ui/utils'
import {
  BarChart3,
  Blocks,
  ChartNoAxesCombined,
  ChevronRight,
  FileBarChart,
  FileCog,
  FileWarning,
  Globe,
  Handshake,
  PackageSearch,
  Smartphone,
  Users2,
  WandSparkles,
} from 'lucide-react'
import { ComponentType, SVGProps } from 'react'

import { useAbility } from '@/ability/ability-context'
import { LinkItem, LinkItemProps } from '@/components/layouts/sidebar/LinkItem'
import { buildSearchParam, SearchParamsViewNames } from '@/routes'
import { useFeatureFlags } from '@/store/feature-flags/hooks'
import { getCurrentOrganization } from '@/store/helpers'

function LinksGroup({
  links,
  label,
  icon: Icon,
  sortByLabels = false,
}: {
  links: LinkItemProps[]
  label?: string
  icon?: ComponentType<SVGProps<SVGSVGElement>>
  sortByLabels?: boolean
}) {
  const { hasFeature } = useFeatureFlags()
  const isOneLinkActive = links.some((link) => link.isActive)
  const hasAtLeastOneLinkAvailable = links.some(
    (link) => link.feature === 'any' || hasFeature(link.feature),
  )
  const sortedLinks = sortByLabels ? [...links].sort(sortByKey('label')) : links
  if (!label || !hasAtLeastOneLinkAvailable) return null
  return (
    <Collapsible asChild defaultOpen={isOneLinkActive}>
      <>
        <CollapsibleTrigger asChild className="group/collapsible w-full">
          <div
            className={cn(
              'data-[state=open]:bg-primary hover:bg-primary flex w-full cursor-pointer items-center gap-2 rounded-md px-5 py-2 transition-colors duration-500',
            )}
          >
            {Icon && <Icon className="size-5" />}
            {label}
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-2 border-x-2 border-b-2 border-primary rounded-md">
          {sortedLinks.map((link, index) => (
            <LinkItemWrapper key={index} link={link} />
          ))}
        </CollapsibleContent>
      </>
    </Collapsible>
  )
}

function LinkItemWrapper({ link }: { link: LinkItemProps }) {
  const { hasFeature } = useFeatureFlags()
  if (link.feature === 'any' || hasFeature(link.feature)) return <LinkItem {...link} />
}

export function AdminMenu({ currentKey, keyName }: { currentKey: string | null; keyName: string }) {
  const { t, tc } = useT({ keyPrefix: 'adminMenu' })
  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])

  const ability = useAbility()

  const displayCommonSources = organization && Object.keys(organization.commonSources).length > 0

  const menu: (
    | {
        links: LinkItemProps[]
        label: string
        sorted?: boolean
        icon?: ComponentType<SVGProps<SVGSVGElement>>
      }
    | {
        link: LinkItemProps
        icon?: ComponentType<SVGProps<SVGSVGElement>>
      }
  )[] = [
    {
      label: tc('organization', { cfl: true }),
      icon: FileCog,
      links: [
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.ACCOUNT),
          label: tc('account', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.ACCOUNT,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.DESCRIPTION),
          label: tc('description', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.DESCRIPTION,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.STRUCTURED_DATA),
          label: tc('structuredData', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.STRUCTURED_DATA,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.CATEGORIES),
          label: tc('categories', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.CATEGORIES,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.METHODOLOGY),
          label: tc('methodology', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.METHODOLOGY,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_PROFILE_TEMPLATE),
          label: tc('beneficiaryProfileTemplate', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.BENEFICIARY_PROFILE_TEMPLATE,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.MEMBERS),
          label: tc('members', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.MEMBERS,
        },
      ],
    },
    {
      link: {
        feature: 'any',
        to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_LIST),
        label: tc('beneficiaries', { cfl: true }),
        icon: Users2,
        isActive: currentKey === SearchParamsViewNames.BENEFICIARY_LIST,
      },
    },
    {
      icon: Blocks,
      label: t('sources'),
      links: [
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.SOURCES_PRIVATE),
          icon: PackageSearch,
          label: t('private'),
          isActive: currentKey === SearchParamsViewNames.SOURCES_PRIVATE,
        },
        {
          feature: 'any',
          to: '#',
          icon: Globe,
          label: t('public'),
          isDisabled: true,
        },
        {
          feature: 'any',
          to: displayCommonSources
            ? buildSearchParam(keyName, SearchParamsViewNames.SOURCES_COMMON)
            : '#',
          icon: Handshake,
          label: t('common'),
          isActive: currentKey === SearchParamsViewNames.SOURCES_COMMON,
          isDisabled: !displayCommonSources,
        },
      ],
    },
    {
      label: t('beneficiaryApp'),
      icon: Smartphone,
      links: [
        {
          feature: 'chat',
          to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_APP),
          label: t('interfaceSettings'),
          isActive: currentKey === SearchParamsViewNames.BENEFICIARY_APP,
        },
        {
          feature: 'chat',
          to: buildSearchParam(keyName, SearchParamsViewNames.CHAT_MESSAGE_SETTINGS),
          label: t('chatMessageSettings'),
          isActive: currentKey === SearchParamsViewNames.CHAT_MESSAGE_SETTINGS,
        },
        {
          feature: 'chat',
          to: buildSearchParam(keyName, SearchParamsViewNames.FEEDBACKS),
          label: tc('feedbacks'),
          isActive: currentKey === SearchParamsViewNames.FEEDBACKS,
          isDisabled: !(
            organization &&
            organization.chatSettings &&
            organization.chatSettings.allowFeedback
          ),
        },
      ],
    },
  ]

  if (ability.can('manage', 'Insights')) {
    menu.push({
      label: t('analytics.menu.title'),
      icon: BarChart3,
      links: [
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.INSIGHTS_DASHBOARD),
          icon: ChartNoAxesCombined,
          label: t('analytics.menu.dashboard', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.INSIGHTS_DASHBOARD,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.INSIGHTS_LIST),
          icon: FileBarChart,
          label: t('analytics.menu.list', { cfl: true }),
          isActive:
            currentKey === SearchParamsViewNames.INSIGHTS_LIST ||
            currentKey === SearchParamsViewNames.INSIGHTS_NEW ||
            currentKey === SearchParamsViewNames.INSIGHTS_EDIT,
        },
        {
          feature: 'any',
          to: buildSearchParam(keyName, SearchParamsViewNames.INSIGHTS_CHAT),
          icon: WandSparkles,
          label: t('analytics.menu.oracle', { cfl: true }),
          isActive: currentKey === SearchParamsViewNames.INSIGHTS_CHAT,
        },
      ],
    })
  }

  if (
    ability.can('manage', 'all') &&
    !menu.find((item) => 'label' in item && item.label === t('prompts'))
  )
    menu.push({
      label: tc('prompts', { cfl: true }),
      icon: FileWarning,
      sorted: true,
      links: [
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.FILE_SUMMARY_SUGGEST,
          label: t('prompt_FILE_SUMMARY_SUGGEST'),
          to: buildSearchParam(keyName, SearchParamsViewNames.FILE_SUMMARY_SUGGEST),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.FILE_IMPORT,
          label: t('prompt_FILE_IMPORT'),
          to: buildSearchParam(keyName, SearchParamsViewNames.FILE_IMPORT),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.RESOURCES_FILE_LOAD,
          label: t('prompt_RESOURCES_FILE_LOAD'),
          to: buildSearchParam(keyName, SearchParamsViewNames.RESOURCES_FILE_LOAD),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.BENEFICIARY_PROFILE_FILE_LOAD,
          label: t('prompt_BENEFICIARY_PROFILE_FILE_LOAD'),
          to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_PROFILE_FILE_LOAD),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.ORGANIZATION_DESCRIPTION,
          label: t('prompt_ORGANIZATION_DESCRIPTION'),
          to: buildSearchParam(keyName, SearchParamsViewNames.ORGANIZATION_DESCRIPTION),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.ORGANIZATION_METHODOLOGY,
          label: t('prompt_ORGANIZATION_METHODOLOGY'),
          to: buildSearchParam(keyName, SearchParamsViewNames.ORGANIZATION_METHODOLOGY),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.CASEWORKER_DASHBOARD_SUGGEST,
          label: t('prompt_CASEWORKER_DASHBOARD_SUGGEST'),
          to: buildSearchParam(keyName, SearchParamsViewNames.CASEWORKER_DASHBOARD_SUGGEST),
        },
        {
          feature: 'chat',
          isActive: currentKey === SearchParamsViewNames.CHAT_WELCOME,
          label: t('prompt_CHAT_WELCOME'),
          to: buildSearchParam(keyName, SearchParamsViewNames.CHAT_WELCOME),
        },
        {
          feature: 'chat',
          isActive: currentKey === SearchParamsViewNames.CHAT_CONVERSE,
          label: t('prompt_CHAT_CONVERSE'),
          to: buildSearchParam(keyName, SearchParamsViewNames.CHAT_CONVERSE),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.BENEFICIARY_PROFILE,
          label: t('prompt_BENEFICIARY_PROFILE'),
          to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_PROFILE),
        },
        {
          feature: 'any',
          isActive: currentKey === SearchParamsViewNames.BENEFICIARY_STRUCTURED_DATA,
          label: t('prompt_BENEFICIARY_STRUCTURED_DATA'),
          to: buildSearchParam(keyName, SearchParamsViewNames.BENEFICIARY_STRUCTURED_DATA),
        },
      ],
    })

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="px-4">
        <div className="flex flex-1 flex-col gap-1">
          {menu.map((item, index) => {
            if ('link' in item) {
              return <LinkItem key={index} {...item.link} />
            }
            if ('links' in item)
              return (
                <LinksGroup
                  key={index}
                  label={item.label}
                  links={item.links}
                  icon={item.icon}
                  sortByLabels={item.sorted}
                />
              )
            return
          })}
        </div>
      </ScrollArea>
    </div>
  )
}
